import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { popupApi, popupErrorMessage, EventPopup } from '../../services/api/popupApi';
import EventPopupDialog from '../../components/EventPopupDialog';
import { fileToPopupImage } from './popupImage';
import { emptyPopupForm, formFromPopup, inputFromForm, popupStatus, PopupFormState, PopupStatus } from './popupForm';

const STATUS_COLOR: Record<PopupStatus, 'success' | 'info' | 'default' | 'warning'> = {
  '게시 중': 'success',
  '예약': 'info',
  '종료': 'default',
  '꺼짐': 'warning',
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

// 첫 화면 팝업 관리 (/admin/popups). 관리자 화면에 본부 포털 계정으로 로그인해 있어야 저장·수정·삭제가 된다
// (nginx 와 event-service 가 로그인 세션을 확인한다 — docs/메인_관리자_로그인.md).
const PopupManagePage: React.FC = () => {
  const [popups, setPopups] = useState<EventPopup[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [editing, setEditing] = useState<EventPopup | 'new' | null>(null);
  const [form, setForm] = useState<PopupFormState>(emptyPopupForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<EventPopup | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      setPopups(await popupApi.listAll());
    } catch (err: any) {
      setListError(popupErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm(emptyPopupForm());
    setFormError('');
    setEditing('new');
  };
  const openEdit = (p: EventPopup) => {
    setForm(formFromPopup(p));
    setFormError('');
    setEditing(p);
  };
  const set = (patch: Partial<PopupFormState>) => setForm((f) => ({ ...f, ...patch }));

  const pickImage = async (file: File | undefined) => {
    if (!file) return;
    setFormError('');
    try {
      set({ image: await fileToPopupImage(file) });
    } catch (err: any) {
      setFormError(err.message);
    }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setFormError('');
    try {
      const input = inputFromForm(form);
      if (editing === 'new') await popupApi.create(input);
      else await popupApi.update(editing.id, input);
      setEditing(null);
      await load();
    } catch (err: any) {
      setFormError(popupErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p: EventPopup) => {
    if (!window.confirm(`「${p.title}」 팝업을 지울까요? 되돌릴 수 없습니다.`)) return;
    try {
      await popupApi.remove(p.id);
      await load();
    } catch (err: any) {
      setListError(popupErrorMessage(err));
    }
  };

  const editingImage = form.image || (editing && editing !== 'new' ? editing.imageUrl : '');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5">팝업 관리</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>새 팝업</Button>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        awanaevent.com 첫 화면에 뜨는 팝업입니다. 켜져 있고 기간 안인 팝업이 순서(작은 수부터)대로 한 장씩 뜹니다.
        참가자가 「오늘 하루 보지 않기」를 누르면 그 팝업은 그날 다시 뜨지 않습니다.
      </Typography>
      {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>그림</TableCell>
              <TableCell>제목</TableCell>
              <TableCell>기간</TableCell>
              <TableCell>상태</TableCell>
              <TableCell align="right">순서</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {popups.map((p) => {
              const status = popupStatus(p);
              return (
                <TableRow key={p.id}>
                  <TableCell>
                    <Box component="img" src={p.imageUrl} alt="" sx={{ height: 64, borderRadius: 1, display: 'block' }} />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{p.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.linkUrl || '링크 없음'}</Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(p.startAt)}<br />~ {fmt(p.endAt)}</TableCell>
                  <TableCell><Chip size="small" label={status} color={STATUS_COLOR[status]} /></TableCell>
                  <TableCell align="right">{p.order}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" onClick={() => setPreview(p)}>미리 보기</Button>
                    <Button size="small" onClick={() => openEdit(p)}>수정</Button>
                    <Button size="small" color="error" onClick={() => remove(p)}>삭제</Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && popups.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  올린 팝업이 없습니다. 「새 팝업」으로 올리세요.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!editing} onClose={() => !saving && setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing === 'new' ? '새 팝업' : '팝업 수정'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="제목" required value={form.title} onChange={(e) => set({ title: e.target.value })} inputProps={{ maxLength: 60 }} />
            <TextField label="안내 문구" multiline minRows={2} value={form.text} onChange={(e) => set({ text: e.target.value })} inputProps={{ maxLength: 300 }} />
            <Stack direction="row" spacing={2}>
              <TextField label="버튼 글자" value={form.buttonText} onChange={(e) => set({ buttonText: e.target.value })} inputProps={{ maxLength: 20 }} sx={{ flex: 1 }} />
              <TextField label="순서" type="number" value={form.order} onChange={(e) => set({ order: e.target.value })} sx={{ width: 120 }} />
            </Stack>
            <TextField
              label="링크 주소"
              value={form.linkUrl}
              onChange={(e) => set({ linkUrl: e.target.value })}
              helperText="버튼·그림을 누르면 갈 곳. /retreat/info/ 처럼 / 로 시작하는 이 사이트 주소나 https:// 주소. 비우면 버튼 없이 그림만 보입니다."
            />
            <Stack direction="row" spacing={2}>
              <TextField label="시작" type="datetime-local" value={form.start} onChange={(e) => set({ start: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
              <TextField label="끝" type="datetime-local" value={form.end} onChange={(e) => set({ end: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
            </Stack>
            <FormControlLabel control={<Switch checked={form.active} onChange={(e) => set({ active: e.target.checked })} />} label="켜기 (기간 안이면 첫 화면에 뜬다)" />
            <Box>
              <Button component="label" variant="outlined">
                {editingImage ? '그림 바꾸기' : '그림 고르기'}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  aria-label="그림 파일"
                  onChange={(e) => {
                    pickImage(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                포스터 그림(JPG·PNG). 가로 1000px 로 줄여서 올립니다.
              </Typography>
              {editingImage && (
                <Box
                  component="img"
                  src={editingImage}
                  alt={form.image ? '새로 고른 그림' : '지금 그림'}
                  sx={{ display: 'block', mt: 1, maxHeight: 220, maxWidth: '100%', borderRadius: 1 }}
                />
              )}
            </Box>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>취소</Button>
          <Button variant="contained" onClick={save} disabled={saving}>저장</Button>
        </DialogActions>
      </Dialog>

      {preview && (
        <EventPopupDialog popup={preview} open onClose={() => setPreview(null)} onHideToday={() => setPreview(null)} />
      )}
    </Container>
  );
};

export default PopupManagePage;
